import { useEffect, useMemo, useState } from 'react'
import { Menu, Search, ChevronLeft, ChevronRight, X } from 'lucide-react'
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet'
import { OrganizationSidebarContent } from '@/components/OrganizationSidebar'
import { Button } from '@/components/ui/button'
import { PhoneNumberInput } from '@/components/PhoneNumberInput'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { authApi, organizationApi, userApi, type OrganizationUser } from '@/utils/api'
import { useAuth } from '@/context/AuthContext'
import { getUserDisplayName, getUserInitials } from '@/utils/user-display'

type StatusType = 'Active' | 'Pending'

const filters = ['All', 'Pending Acceptance']

const formatDate = (value?: string | null) => {
  if (!value) return '-'
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return '-'
  return parsed
    .toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    })
    .replace(/ /g, '-')
}

const formatRole = (value?: string | null) => {
  if (!value) return '-'
  return value
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase())
}

const resolveUserType = (user: OrganizationUser) => {
  if (user.systemRole) return formatRole(user.systemRole)
  const fallback = user.role || ''
  const systemRoles = new Set(['super_admin', 'organization_admin', 'counselor', 'couple'])
  return systemRoles.has(fallback) ? formatRole(fallback) : '-'
}

const getStatus = (user: OrganizationUser): StatusType => {
  if (!user.isActive) return 'Pending'
  if (user.isPhoneVerified === false) return 'Pending'
  return 'Active'
}

const formatName = (user: OrganizationUser) => {
  const name = [user.firstName, user.lastName].filter(Boolean).join(' ').trim()
  return name || user.email || user.phoneNumber || 'Unknown'
}

const getDefaultSystemRole = (role?: string | null) =>
  role === 'super_admin' ? '' : 'organization_admin'

const buildDefaultFormData = (role?: string | null) => ({
  firstName: '',
  lastName: '',
  role: '',
  systemRole: getDefaultSystemRole(role),
  email: '',
  phone: '',
})

const buildPagination = (current: number, total: number) => {
  if (total <= 7) {
    return Array.from({ length: total }, (_, idx) => idx + 1)
  }
  const pages: Array<number | 'ellipsis'> = [1]
  const start = Math.max(2, current - 1)
  const end = Math.min(total - 1, current + 1)

  if (start > 2) pages.push('ellipsis')
  for (let page = start; page <= end; page += 1) pages.push(page)
  if (end < total - 1) pages.push('ellipsis')
  pages.push(total)

  return pages
}

export default function InternalUserManagement() {
  const { user } = useAuth()
  const displayName = getUserDisplayName(user)
  const initials = getUserInitials(user)
  const isSuperAdmin = user?.role === 'super_admin'
  const [users, setUsers] = useState<OrganizationUser[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const [actionLoadingId, setActionLoadingId] = useState<string | null>(null)
  const [selectedFilter, setSelectedFilter] = useState<string>('All')
  const [searchQuery, setSearchQuery] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false)
  const [inviteError, setInviteError] = useState<string | null>(null)
  const [inviteLoading, setInviteLoading] = useState(false)
  const [formData, setFormData] = useState(() => buildDefaultFormData(user?.role))

  const getStatusBadge = (status: StatusType) => {
    const styles = {
      Active: 'bg-[#aee753] text-[#4b6029]',
      Pending: 'bg-[#ffedd5] text-[#ea580c]',
    }

    return (
      <span className={`px-3 py-1 rounded-[5px] text-[10px] font-medium ${styles[status]}`}>
        {status}
      </span>
    )
  }

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }))
  }

  const fetchUsers = async () => {
    if (!user?.organizationId) {
      setError('No organization found for this account.')
      return
    }
    try {
      setLoading(true)
      setError(null)
      const response = await organizationApi.getOrganization(user.organizationId)
      const payload =
        (response as { data?: { users?: OrganizationUser[] } }).data ||
        (response as unknown as { users?: OrganizationUser[] })
      setUsers(Array.isArray(payload?.users) ? payload.users : [])
    } catch (err) {
      console.error('Failed to load organization users:', err)
      setError((err as Error)?.message || 'Failed to load users')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void fetchUsers()
  }, [user?.organizationId])

  useEffect(() => {
    setCurrentPage(1)
  }, [selectedFilter, searchQuery, rowsPerPage])

  const filteredUsers = useMemo(() => {
    const query = searchQuery.trim().toLowerCase()
    return users.filter((item) => {
      const status = getStatus(item)
      if (selectedFilter === 'Pending Acceptance' && status !== 'Pending') {
        return false
      }
      if (!query) return true
      const haystack = [
        formatName(item),
        item.email,
        item.phoneNumber,
        formatRole(item.role),
        resolveUserType(item),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
      return haystack.includes(query)
    })
  }, [users, searchQuery, selectedFilter])

  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / rowsPerPage))
  const safePage = Math.min(currentPage, totalPages)

  useEffect(() => {
    if (currentPage !== safePage) {
      setCurrentPage(safePage)
    }
  }, [currentPage, safePage])

  const pagedUsers = useMemo(() => {
    const start = (safePage - 1) * rowsPerPage
    return filteredUsers.slice(start, start + rowsPerPage)
  }, [filteredUsers, rowsPerPage, safePage])

  const paginationItems = useMemo(
    () => buildPagination(safePage, totalPages),
    [safePage, totalPages]
  )

  const handleInviteUser = async () => {
    if (!user?.organizationId) {
      setInviteError('No organization found for this account.')
      return
    }
    const systemRole = formData.systemRole.trim()
    if (!isSuperAdmin && !systemRole) {
      setInviteError('System role is required.')
      return
    }
    setInviteError(null)
    setInviteLoading(true)
    try {
      const payload = {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        email: formData.email.trim(),
        phoneNumber: formData.phone.trim(),
        role: formData.role.trim(),
        ...(systemRole ? { systemRole } : {}),
      }
      await organizationApi.inviteUser(user.organizationId, payload)
      setIsInviteModalOpen(false)
      setFormData(buildDefaultFormData(user?.role))
      await fetchUsers()
    } catch (err) {
      setInviteError((err as Error)?.message || 'Unable to invite user')
    } finally {
      setInviteLoading(false)
    }
  }

  const handleAction = async (item: OrganizationUser) => {
    if (!item.id) return
    setActionError(null)
    setActionLoadingId(item.id)
    try {
      const status = getStatus(item)
      if (status === 'Active') {
        await userApi.deactivate(item.id)
      } else if (item.isActive === false) {
        await userApi.activate(item.id)
      } else if (item.phoneNumber) {
        await authApi.sendOtp(item.phoneNumber)
      } else {
        setActionError('Missing phone number for reinvite.')
      }
      await fetchUsers()
    } catch (err) {
      setActionError((err as Error)?.message || 'Unable to update user status')
    } finally {
      setActionLoadingId(null)
    }
  }

  return (
    <div className='min-h-screen bg-[#edeff3]'>
      {/* Main Content */}
      <div className='p-4 md:p-6 lg:p-8'>
        {/* Single White Container for entire page */}
        <div className='bg-white rounded-[24px] p-6 md:p-8'>
          {/* Top Section - User Profile */}
          <div className='flex items-center justify-end mb-8'>
            {/* User Profile (Desktop) */}
            <div className='hidden lg:flex items-center gap-3'>
              <div className='flex items-center gap-3'>
                <div className='h-[47px] w-[48px] rounded-full bg-[#434343] flex items-center justify-center text-white font-bold text-[16px]'>
                  {initials}
                </div>
                <span className='text-[24px] font-bold text-[#353535]'>{displayName}</span>
              </div>
              <button className='w-[41px] h-[39px] bg-[#f7f8fa] rounded-full flex items-center justify-center'>
                <svg className='h-4 w-4 text-gray-600 rotate-90' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                  <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z' />
                </svg>
              </button>
            </div>

            {/* Mobile Menu Button */}
            <Sheet>
              <SheetTrigger asChild>
                <button className='lg:hidden p-2 hover:bg-gray-100 rounded-lg transition-colors'>
                  <Menu className='h-6 w-6 text-gray-700' />
                </button>
              </SheetTrigger>
              <SheetContent side='left' className='p-0 w-[280px]'>
                <OrganizationSidebarContent />
              </SheetContent>
            </Sheet>
          </div>

          {/* Page Title and Description */}
          <div className='mb-6 text-start'>
            <h1 className='text-[32px] font-bold text-[#151515] leading-[30px] mb-3'>
              Internal User Management
            </h1>
            <p className='text-[14px] text-[#747474] font-normal leading-6'>
              Lorem ipsum dolor sit amet consectetur. Id tempor malesuada dignissim nam
              feugiat donec mauris proin cum. Aliquet cras consectetur risus mi tortor
              adipiscing. Felis sodales lectus a lectus risus. Sed elementum congue.
            </p>
          </div>

          {/* Search, Filters and Invite Button */}
          <div className='flex flex-col md:flex-row md:items-center gap-4 mb-6'>
            {/* Search */}
            <div className='relative w-[240px]'>
              <Search className='absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#e0e0e0]' />
              <input
                type='text'
                placeholder='Search...'
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className='w-full h-[40px] pl-10 pr-4 border border-[#e0e0e0] rounded-[8px] text-[14px] text-[#151515] placeholder:text-[#e0e0e0] focus:outline-none focus:ring-2 focus:ring-[#FF6600] focus:border-transparent'
              />
            </div>

            {/* Filter Tabs */}
            <div className='flex gap-4'>
              {filters.map((filter) => (
                <button
                  key={filter}
                  onClick={() => setSelectedFilter(filter)}
                  className={`h-[40px] px-4 rounded-[8px] text-[14px] font-bold transition-colors ${
                    selectedFilter === filter
                      ? 'bg-[#151515] text-white'
                      : 'bg-white text-[#151515] border border-[#e0e0e0] hover:bg-gray-50'
                  }`}
                >
                  {filter}
                </button>
              ))}
            </div>

            {/* Invite User Button */}
            <Button
              onClick={() => {
                setInviteError(null)
                setFormData(buildDefaultFormData(user?.role))
                setIsInviteModalOpen(true)
              }}
              className='ml-auto h-[40px] bg-[#151515] hover:bg-[#2a2a2a] text-[#f1f1f1] px-4 rounded-[8px] text-[14px] font-bold'
            >
              Invite User
            </Button>
          </div>

          {(error || actionError) && (
            <div className='mb-4 text-[14px] text-red-600'>
              {error || actionError}
            </div>
          )}

          {/* Table Container */}
          <div className='bg-[rgba(237,239,243,0.5)] rounded-[8px] p-2'>
              <Table>
                <TableHeader>
                  <TableRow className='border-0'>
                    <TableHead className='w-12 py-2'>
                      <input type='checkbox' className='w-4 h-4 rounded border-[#e0e0e0] border-[1.5px]' />
                    </TableHead>
                    <TableHead className='font-bold text-[14px] text-[#232323] py-2'>
                      <div className='flex items-center gap-2'>
                        S/N
                        <svg className='h-3 w-3' viewBox='0 0 12 12' fill='none'>
                          <path d='M6 1v10M6 1L3 4M6 1l3 3M6 11L3 8M6 11l3-3' stroke='currentColor' strokeWidth='1.5' strokeLinecap='round' strokeLinejoin='round'/>
                        </svg>
                      </div>
                    </TableHead>
                    <TableHead className='font-bold text-[14px] text-[#232323] py-2'>
                      <div className='flex items-center gap-2'>
                        Name
                        <svg className='h-3 w-3' viewBox='0 0 12 12' fill='none'>
                          <path d='M6 1v10M6 1L3 4M6 1l3 3M6 11L3 8M6 11l3-3' stroke='currentColor' strokeWidth='1.5' strokeLinecap='round' strokeLinejoin='round'/>
                        </svg>
                      </div>
                    </TableHead>
                    <TableHead className='font-bold text-[14px] text-[#232323] py-2'>
                      <div className='flex items-center gap-2'>
                        Request Date
                        <svg className='h-3 w-3' viewBox='0 0 12 12' fill='none'>
                          <path d='M6 1v10M6 1L3 4M6 1l3 3M6 11L3 8M6 11l3-3' stroke='currentColor' strokeWidth='1.5' strokeLinecap='round' strokeLinejoin='round'/>
                        </svg>
                      </div>
                    </TableHead>
                    <TableHead className='font-bold text-[14px] text-[#232323] py-2'>
                      <div className='flex items-center gap-2'>
                        Role
                        <svg className='h-3 w-3' viewBox='0 0 12 12' fill='none'>
                          <path d='M6 1v10M6 1L3 4M6 1l3 3M6 11L3 8M6 11l3-3' stroke='currentColor' strokeWidth='1.5' strokeLinecap='round' strokeLinejoin='round'/>
                        </svg>
                      </div>
                    </TableHead>
                    <TableHead className='font-bold text-[14px] text-[#232323] py-2'>
                      <div className='flex items-center gap-2'>
                        User Type
                        <svg className='h-3 w-3' viewBox='0 0 12 12' fill='none'>
                          <path d='M6 1v10M6 1L3 4M6 1l3 3M6 11L3 8M6 11l3-3' stroke='currentColor' strokeWidth='1.5' strokeLinecap='round' strokeLinejoin='round'/>
                        </svg>
                      </div>
                    </TableHead>
                    <TableHead className='font-bold text-[14px] text-[#232323] py-2'>
                      <div className='flex items-center gap-2'>
                        Status
                        <svg className='h-3 w-3' viewBox='0 0 12 12' fill='none'>
                          <path d='M6 1v10M6 1L3 4M6 1l3 3M6 11L3 8M6 11l3-3' stroke='currentColor' strokeWidth='1.5' strokeLinecap='round' strokeLinejoin='round'/>
                        </svg>
                      </div>
                    </TableHead>
                    <TableHead className='font-bold text-[14px] text-[#232323] py-2'>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading && (
                    <TableRow>
                      <TableCell colSpan={8} className='text-center text-sm text-gray-600 py-6'>
                        Loading users...
                      </TableCell>
                    </TableRow>
                  )}
                  {!loading && pagedUsers.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8} className='text-center text-sm text-gray-600 py-6'>
                        No users found.
                      </TableCell>
                    </TableRow>
                  )}
                  {!loading && pagedUsers.map((row, index) => {
                    const status = getStatus(row)
                    const rowNumber = (safePage - 1) * rowsPerPage + index + 1
                    const actionLabel = status === 'Active' ? 'Block' : 'Reinvite'
                    const isLoading = actionLoadingId === row.id
                    return (
                      <TableRow key={row.id} className='bg-white rounded-[8px] mb-2 border-0'>
                        <TableCell className='py-3'>
                          <input type='checkbox' className='w-4 h-4 rounded border-[#e0e0e0] border-[1.5px]' />
                        </TableCell>
                        <TableCell className='font-normal text-[14px] text-[#232323] py-3 text-start'>{rowNumber}</TableCell>
                        <TableCell className='font-normal text-[14px] text-[#232323] py-3 text-start'>{formatName(row)}</TableCell>
                        <TableCell className='font-normal text-[14px] text-[#232323] py-3 text-start'>{formatDate(row.createdAt)}</TableCell>
                        <TableCell className='font-normal text-[14px] text-[#232323] py-3 text-start'>{formatRole(row.role)}</TableCell>
                        <TableCell className='font-normal text-[14px] text-[#232323] py-3 text-start'>{resolveUserType(row)}</TableCell>
                        <TableCell className='py-3 text-start'>{getStatusBadge(status)}</TableCell>
                        <TableCell className='py-3 text-start'>
                          <Button
                            onClick={() => handleAction(row)}
                            disabled={isLoading}
                            className='bg-[#151515] hover:bg-[#2a2a2a] text-white px-4 py-2 rounded-[8px] text-[14px] font-semibold h-[40px] disabled:opacity-70'
                          >
                            {isLoading ? 'Working...' : actionLabel}
                          </Button>
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>

              {/* Pagination */}
              <div className='flex items-center justify-between px-2 py-4 mt-4'>
                <div className='flex items-center gap-2'>
                  <span className='text-[12px] text-[#434343] font-normal'>Rows per page:</span>
                  <select
                    value={rowsPerPage}
                    onChange={(e) => setRowsPerPage(Number(e.target.value))}
                    className='h-[32px] border border-[#e0e0e0] rounded-[8px] px-2 text-[12px] text-[#434343] focus:outline-none focus:ring-2 focus:ring-[#FF6600]'
                  >
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                  </select>
                </div>

                <div className='flex items-center gap-1'>
                  <button
                    className='p-1 border border-[#999ca0] rounded-[8px] hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed'
                    onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                    disabled={safePage <= 1}
                    aria-label='Previous page'
                  >
                    <ChevronLeft className='h-4 w-4 text-[#232323]' />
                  </button>
                  {paginationItems.map((item, idx) => {
                    if (item === 'ellipsis') {
                      return (
                        <span key={`ellipsis-${idx}`} className='px-2 text-[14px] text-[#232323]'>
                          ...
                        </span>
                      )
                    }
                    const isActive = item === safePage
                    return (
                      <button
                        key={item}
                        onClick={() => setCurrentPage(item)}
                        className={`h-[32px] px-3 text-[14px] rounded-[8px] ${
                          isActive ? 'bg-[#fa6017] text-white' : 'text-[#232323] hover:bg-gray-100'
                        }`}
                      >
                        {item}
                      </button>
                    )
                  })}
                  <button
                    className='p-1 border border-[#999ca0] rounded-[8px] hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed'
                    onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
                    disabled={safePage >= totalPages}
                    aria-label='Next page'
                  >
                    <ChevronRight className='h-4 w-4 text-[#232323]' />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

      {/* Invite User Side Modal */}
      {isInviteModalOpen && (
        <>
          {/* Backdrop */}
          <div
            className='fixed inset-0 bg-black/50 z-40 transition-opacity'
            onClick={() => setIsInviteModalOpen(false)}
          />

          {/* Right Side Modal */}
          <div className='fixed inset-y-0 right-0 z-50 w-full sm:w-[480px] md:w-[520px] lg:w-[560px] pointer-events-none'>
            <div className='bg-white h-full shadow-2xl pointer-events-auto flex flex-col'>
            {/* Modal Header */}
            <div className='sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between shrink-0'>
              <h2 className='text-2xl font-bold text-gray-900'>Invite Users</h2>
              <button
                onClick={() => setIsInviteModalOpen(false)}
                className='p-2 hover:bg-gray-100 rounded-lg transition-colors'
              >
                <X className='h-6 w-6 text-gray-600' />
              </button>
            </div>

            {/* Modal Content */}
            <div className='flex-1 overflow-y-auto p-6 space-y-6'>
              {/* First Name */}
              <div>
                <label className='block text-sm font-semibold text-gray-900 mb-2 text-start'>
                  First Name
                </label>
                <input
                  type='text'
                  placeholder='Success'
                  value={formData.firstName}
                  onChange={(e) => handleInputChange('firstName', e.target.value)}
                  className='w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent'
                />
              </div>

              {/* Last Name */}
              <div>
                <label className='block text-sm font-semibold text-gray-900 mb-2 text-start'>
                  Last Name
                </label>
                <input
                  type='text'
                  placeholder='Olatunde'
                  value={formData.lastName}
                  onChange={(e) => handleInputChange('lastName', e.target.value)}
                  className='w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent'
                />
              </div>

              {/* Role */}
              <div>
                <label className='block text-sm font-semibold text-gray-900 mb-2 text-start'>Role</label>
                <select
                  value={formData.role}
                  onChange={(e) => handleInputChange('role', e.target.value)}
                  className='w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent text-gray-500'
                >
                  <option value=''>Select role</option>
                  <option value='Marriage Counsel'>Marriage Counsel</option>
                  <option value='Resident Pastor'>Resident Pastor</option>
                  <option value='Pastor'>Pastor</option>
                  <option value='Apostle'>Apostle</option>
                  <option value='Reverend Father'>Reverend Father</option>
                </select>
              </div>

              {/* System Role */}
              <div>
                <label className='block text-sm font-semibold text-gray-900 mb-2 text-start'>
                  System Role
                </label>
                {isSuperAdmin ? (
                  <select
                    value={formData.systemRole}
                    onChange={(e) => handleInputChange('systemRole', e.target.value)}
                    className='w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent text-gray-500'
                  >
                    <option value=''>Optional</option>
                    <option value='counselor'>Counselor</option>
                    <option value='organization_admin'>Organization Admin</option>
                    <option value='super_admin'>Super Admin</option>
                  </select>
                ) : (
                  <input
                    type='text'
                    value='Organization Admin'
                    disabled
                    className='w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-500'
                  />
                )}
              </div>

              {/* Email */}
              <div>
                <label className='block text-sm font-semibold text-gray-900 mb-2 text-start'>Email</label>
                <input
                  type='email'
                  placeholder='johndoe@mail.com'
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  className='w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent'
                />
              </div>

              {/* Phone Number */}
              <div>
                <label className='block text-sm font-semibold text-gray-900 mb-2 text-start'>
                  Phone Number
                </label>
                <PhoneNumberInput
                  value={formData.phone}
                  onChange={(value) => handleInputChange('phone', value)}
                  placeholder='Phone number'
                  containerClassName='bg-gray-50 border-gray-200'
                  inputClassName='py-0'
                />
              </div>

              {inviteError && (
                <div className='text-sm text-red-600 text-start'>
                  {inviteError}
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className='sticky bottom-0 bg-white border-t border-gray-200 p-6 shrink-0'>
              <Button
                onClick={handleInviteUser}
                disabled={inviteLoading}
                className='w-full bg-gray-900 hover:bg-gray-800 text-white py-4 rounded-lg text-base font-medium'
              >
                {inviteLoading ? 'Inviting...' : 'Invite User'}
              </Button>
            </div>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
